import { Plus, TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EmptyState } from '@/components/feedback/EmptyState'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader, CardSubtitle, CardTitle } from '@/components/ui/Card'
import {
  useAmendExpense,
  useDropExpense,
  useRecordExpense,
} from '@/features/cases/api/useCase'
import { ExpenseFormSheet } from '@/features/cases/components/CaseRecordSheets'
import { refusalMessage } from '@/features/cases/lib/refusal'
import { CasePayments } from '@/features/finance/components/CasePayments'
import { useFormat } from '@/i18n/useFormat'
import { formatNumber } from '@/lib/format'
import { isSuppressed } from '@/types/domain'
import type { CaseDetail, ExpenseLine } from '@/types/domain'

import { BalancesRow, ProfitRow } from './BalanceCells'
import { ExpenseTable } from './ExpenseTable'
import { TrancheStrip } from './TrancheStrip'
import { editing, type EditTarget } from './money'

/* One card, one question: the plan against the fact, line by line, with the
 * three balances that fall out of it along the bottom. The rate tiles that
 * used to open this section were the header band said twice — the band keeps
 * rate/cost/forecast, and the section keeps what the band cannot hold: WHERE
 * the variance comes from and what is still owed on both sides. */

/** The money card — or, when there is no block, the reason there is none.
 *
 *  THREE STATES, which used to be one. The server serves the block whenever it
 *  can compute it and names its reason when it cannot (see
 *  `cmd/asa/readmodel_case.go`), so this section renders:
 *
 *    · the block, whatever its figures;
 *    · "nothing recorded yet" with the action that records something, for a
 *      case nobody has priced — every FR-70 direct creation and all 241
 *      migrated cases start here (UX-DR-17);
 *    · a failure, with a retry, when the read or the FX-rate lookup behind it
 *      failed (UX-DR-18).
 *
 *  What it no longer renders is a permission refusal, because that state does
 *  not exist: AD-5 gives a deal's money to the deal's own team, and a caller who
 *  cannot reach the case gets a 404 on the case itself. The old copy said
 *  "Money is not shown for your role" to a founder looking at a brand-new
 *  shipment, and the two translated keys behind it are deleted rather than left
 *  for a state nothing can produce.
 *
 *  Takes the RECORD rather than the block, like every other section on this
 *  page: it needs the block, the unavailability reason and the case id, and
 *  three props that must agree with each other are one prop. */
export function MoneySection({
  record,
  onRetry,
}: {
  record: CaseDetail
  /** Refetches the case, which is where the money block comes from. The section
   *  has no query of its own — the block rides the composed detail — so the
   *  retry has to be the page's. */
  onRetry?: () => void
}) {
  const { t } = useTranslation()
  const format = useFormat()
  const [target, setTarget] = useState<EditTarget<ExpenseLine>>(null)

  const caseId = record.id
  const money = record.money
  const quoteNumber = record.quoteNumber

  const recordExpense = useRecordExpense(caseId)
  const amendExpense = useAmendExpense(caseId)
  const dropExpense = useDropExpense(caseId)

  const editingExpense = editing(target)
  const expensePending =
    recordExpense.isPending || amendExpense.isPending || dropExpense.isPending
  const expenseFailure =
    recordExpense.error ?? amendExpense.error ?? dropExpense.error

  if (!money) {
    /* No block, and the server said why. Both reasons — `not-configured` and
       `read-failed` — are the same rendering for a founder: something went
       wrong, try again. They are distinguished in the server's log, which is
       where the difference is actionable.

       EmptyState rather than ErrorState, and the choice is deliberate rather
       than lazy: ErrorState derives its words from a problem document, and
       there is no problem document here — the case request SUCCEEDED and only
       this section of it is missing. Handing ErrorState a non-ApiError makes
       describeProblem fall through to its network copy, which would tell the
       reader they are offline while the rest of the page renders fine. */
    return (
      <Card>
        <EmptyState
          icon={TriangleAlert}
          title={t('cases.money.unavailableTitle')}
          description={t('cases.money.unavailableDescription')}
          action={
            onRetry ? (
              <Button variant="secondary" size="sm" onClick={onRetry}>
                {t('common.actions.retry')}
              </Button>
            ) : undefined
          }
        />
      </Card>
    )
  }

  /* Nothing has been recorded against this case in any money table: no cost
     line, no agreed rate, no payment and no expected tranche. Distinguished
     from a refusal and from a failure, and offering the one action that fills
     it (UX-DR-17). The rate is agreed elsewhere — it is Finance's act on the
     record, not a control on this card — so the empty state names it in prose
     rather than offering a button that would not work. */
  const untouched =
    money.hasRate === false &&
    money.expenses.length === 0 &&
    money.tranches.length === 0

  /* The receivable's provenance, read off the tranches: which slice of the rate
     is expected when. Rendered without a paid/unpaid distinction, deliberately —
     per-tranche settlement needs the allocations against that tranche, and the
     block serves only the case's receivable TOTAL. A `settled` flag nothing
     could fill is what made this note render empty for two years' worth of
     fixture; see ReceivableTranche. */
  const trancheNote = money.tranches
    .map((tranche) =>
      tranche.expectedOn
        ? t('cases.money.trancheExpected', {
            n: tranche.sequence,
            amount: formatNumber(tranche.amount.amount, 2),
            date: format.dateShort(tranche.expectedOn),
          })
        : t('cases.money.trancheUndated', {
            n: tranche.sequence,
            amount: formatNumber(tranche.amount.amount, 2),
          }),
    )
    .join(' · ')

  /* Who the payables are owed to: lines with a recorded actual not yet paid
     out. A suppressed vendor keeps its amount and loses only its name; a line
     with no vendor at all names itself. */
  const payablesNote = [
    ...money.expenses
      .filter((expense) => expense.actual && !expense.settled)
      .map(
        (expense) =>
          `${
            isSuppressed(expense.counterpartyName)
              ? t('common.suppressed.vendor')
              : (expense.counterpartyName ?? expense.description)
          } ${formatNumber(expense.actual?.amount, 2)}`,
      ),
    money.payablesDueOn
      ? t('cases.money.dueOn', { date: format.dateShort(money.payablesDueOn) })
      : null,
  ]
    .filter(Boolean)
    .join(' · ')

  /* Hoisted out of the return so both branches below can render it: the empty
     state offers the same "record a cost" action the header does, and an action
     that opened nothing would be the trap the legs section's empty state
     deliberately avoids. */
  const sheet = (
    <ExpenseFormSheet
      open={target !== null}
      onClose={() => setTarget(null)}
      record={editingExpense}
      pending={expensePending}
      error={expenseFailure ? refusalMessage(expenseFailure, t) : undefined}
      onSubmit={(values) => {
        /* The sheet stays OPEN while the save is in flight and stays open
           holding the draft if the server refuses, so nothing typed is lost
           (UX-DR-18) — and for money that matters more than elsewhere: the
           server can refuse a currency it has no cached FX rate for (AD-20),
           which is a failure the typist can do nothing about except retry. */
        const done = { onSuccess: () => setTarget(null) }

        /* An existing line is a PUT on its own id, a new one is a POST. There
           is no longer a question of WHICH row to target: the folded line is
           what the endpoints take, and the server decides which of the two AD-4
           rows the draft means. The three-way branch that used to stand here
           had to guess, and got it wrong three ways — a plan acquiring its
           first actual created a second plan, an edit to both figures saved
           one, and the id it chose was sometimes the actual's.

           `editingExpense.id` is the PLAN's id, which is what both endpoints
           want and what the folded DTO already serves as `id`. */
        if (editingExpense) {
          amendExpense.mutate({ lineId: editingExpense.id, draft: values }, done)
          return
        }

        recordExpense.mutate(values, done)
      }}
      /* Deleting a cost line deletes the LINE — the plan and every actual
         against it, in one transaction, which is what the reader who pressed
         Delete on this row asked for. It used to send `actualId ?? id`, so on a
         line with an invoice it removed the invoice and left the plan sitting
         on screen looking undeleted.

         A second invoice against the plan is NOT a refusal here: the delete
         cascades whatever the count, because "which invoice did you mean" has
         no answer to guess at when both are going. What can still refuse is a
         state of the record — the case closed to money, or the line already
         deleted by somebody else — and that reaches the reader through
         `refusalMessage` with the sheet still open. */
      onDelete={
        editingExpense
          ? () =>
              dropExpense.mutate(editingExpense.id, {
                onSuccess: () => setTarget(null),
              })
          : undefined
      }
    />
  )

  if (untouched) {
    /* NOTHING YET, and said as such. This is the state a case is in from
       creation until somebody prices it or records a cost — every FR-70 direct
       creation and all 241 migrated cases — and it used to render as "money is
       not shown for your role", which sent a founder to ask for a permission
       they already had. */
    return (
      <>
        <Card>
          <CardHeader>
            <CardTitle>{t('cases.money.title')}</CardTitle>
            <CardSubtitle>
              {quoteNumber
                ? t('cases.money.subtitleFromQuote', { number: quoteNumber })
                : t('cases.money.subtitle')}
            </CardSubtitle>
          </CardHeader>
          <EmptyState
            title={t('cases.money.untouchedTitle')}
            description={t('cases.money.untouchedDescription')}
            action={
              <Button size="sm" iconLeft={Plus} onClick={() => setTarget('new')}>
                {t('cases.money.recordActual')}
              </Button>
            }
          />
          {/* Payments keep their own card even here: cash can arrive on a case
              nobody has priced, and the list is where it would show. */}
          <CasePayments caseId={caseId} />
        </Card>
        {sheet}
      </>
    )
  }

  return (
    <>
      <Card>
        <CardHeader
          action={
            <Button
              variant="secondary"
              size="sm"
              iconLeft={Plus}
              onClick={() => setTarget('new')}
            >
              {t('cases.money.recordActual')}
            </Button>
          }
        >
          <CardTitle>{t('cases.money.title')}</CardTitle>
          <CardSubtitle>
            {quoteNumber
              ? t('cases.money.subtitleFromQuote', { number: quoteNumber })
              : t('cases.money.subtitle')}
          </CardSubtitle>
        </CardHeader>

        {money.expenses.length === 0 ? (
          <EmptyState
            title={t('cases.money.expensesEmptyTitle')}
            description={t('cases.money.expensesEmptyDescription')}
            className="py-6"
          />
        ) : (
          /* The route travels with the block: a cost row names the direction it
             paid for by id (FR-67), and the sequence and the plate it renders
             live on the legs. Passed from the RECORD rather than looked up by a
             query of its own, because both are slices of one composed read and
             a second fetch would be a second answer about the same case. */
          <ExpenseTable
            caseId={caseId}
            legs={record.legs}
            money={money}
            pending={expensePending}
            onEdit={(expense) => setTarget(expense)}
            /* The same drop the sheet's own Delete performs, reached from the
               row instead — the line, its plan and every actual against it, in
               one transaction. The row asks before it calls this. */
            onDelete={(expense) => dropExpense.mutate(expense.id)}
          />
        )}

        {/* A refusal from the row's delete has nowhere else to land: the sheet
            renders `expenseFailure`, and an inline delete happens with the
            sheet closed. Without this the line would simply stay on screen and
            the reader would press again. */}
        {target === null && dropExpense.error ? (
          <p className="text-negative px-[18px] pt-3 text-[12.5px] leading-snug" role="alert">
            {refusalMessage(dropExpense.error, t)}
          </p>
        ) : null}

        <BalancesRow
          money={money}
          trancheNote={trancheNote || undefined}
          payablesNote={payablesNote || undefined}
        />

        <ProfitRow money={money} />

        <TrancheStrip caseId={caseId} tranches={money.tranches} />

        {/* The rows every figure above is arithmetic over. Its own query, so a
            payment recorded from the Money page appears here without this card
            refetching the whole case.

            It carries the group's own rule when the strip above it rendered
            nothing — a case whose rate was never agreed has no series, and the
            record group still has to start somewhere visible. */}
        <CasePayments
          caseId={caseId}
          className={money.tranches.length === 0 ? 'border-line' : undefined}
        />
      </Card>

      {sheet}
    </>
  )
}
